import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../../i18n';
import './ClueRevealHost.css';

const DEFAULT_CLUE_INTERVAL_MS = 3000;

export interface ClueRevealHostProps {
  /** All 5 clues in order */
  clues: string[];
  /** Timestamp when the question was shown (e.g. questionStartTime) */
  startTime: number;
  /** Ms between each clue (from backend or getClueIntervalMs(timeLimit)). 20s game → 3000, 30s → 4000. */
  clueIntervalMs?: number;
  /** Optional theme color for borders/accents */
  themeColor?: string;
}

/**
 * Reveals one clue every clueIntervalMs. Used below the question on host (and optionally player) for
 * what-could-it-be, clue-in, what-am-i, guess-what games.
 */
export const ClueRevealHost: React.FC<ClueRevealHostProps> = ({
  clues,
  startTime,
  clueIntervalMs = DEFAULT_CLUE_INTERVAL_MS,
  themeColor = 'var(--color-primary)',
}) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(0);
  const startTimeRef = useRef(startTime);
  const cluesRef = useRef(clues);
  const intervalMsRef = useRef(clueIntervalMs);

  if (cluesRef.current !== clues || startTimeRef.current !== startTime) {
    startTimeRef.current = startTime;
    cluesRef.current = clues;
  }
  intervalMsRef.current = clueIntervalMs;

  useEffect(() => {
    const updateVisible = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const count = Math.min(cluesRef.current.length, Math.max(0, Math.floor(elapsed / intervalMsRef.current)));
      setVisibleCount(count);
    };

    updateVisible();
    const interval = setInterval(updateVisible, 500);
    return () => clearInterval(interval);
  }, [startTime, clues.length, clueIntervalMs]);

  if (!clues.length) return null;

  return (
    <div className="clue-reveal-host" style={{ ['--clue-theme' as string]: themeColor }}>
      <div className="clue-reveal-host__title">{t('gameplay.clueReveal.title')}</div>
      <ul className="clue-reveal-host__list">
        {clues.slice(0, visibleCount).map((clue, index) => (
          <li key={index} className="clue-reveal-host__item">
            <span className="clue-reveal-host__number">{index + 1}.</span>
            <span className="clue-reveal-host__text">{clue}</span>
          </li>
        ))}
      </ul>
      {visibleCount < clues.length && (() => {
        const nextClueAt = startTime + (visibleCount + 1) * clueIntervalMs;
        const remainingMs = nextClueAt - Date.now();
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        return remainingSec > 0 ? (
          <div className="clue-reveal-host__next">
            {t('gameplay.clueReveal.nextIn', { seconds: remainingSec })}
          </div>
        ) : null;
      })()}
    </div>
  );
};
